import './mail-basket.component.ajscss';

import { IBasketStore } from '@agent/core/basket/basket.store';
import { BasketCategoryType, Sort } from '@agent/core/basket/models/basket';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaRequestType } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IOutgoingMailDeprecatedFacade } from '@agent/core/channel-digit/mail/outgoing-mail-deprecated.service';
import { IContactHistoryService } from '@agent/core/contact/history/contact-history.service';
import { ISearchService } from '@agent/core/search/search.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ModalService } from '@commons/core/ui/modal/modal.service';
import { TimelineEntry } from '@commons/history/timeline/models/timeline-entry';
import { IContactEntry } from '@commons/models/contact-entry';
import { ChannelUtil } from '@commons/util/helper/channel';
import { destroy, ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions } from 'angular';
import { i18n } from 'i18next';
import { combineLatest, delay, first, map, of, switchMap, take } from 'rxjs';

import {
  AgentMailFacade,
  AgentUserFacade,
  ChanEnum,
  ChannelType,
  Chosen,
  Configuration,
  deepCopy,
  DelegatorFacade,
  fFalsy,
  joiner,
  LoaderDeprecatedService,
  MailFacade,
  MailUnitFacade,
  MomentService,
  Option,
  ProfileFacade,
  ProfileService,
  Skill,
  ToasterFacade,
  UserUtil,
} from '@contact/upgrade';

import { fromMailBasket, MailBasketAction } from '../';
import templateUrl from './mail-basket.component.ajs.html';
import {
  DelegatorCsrModel,
  INITIAL_STATE_QUICK_FILTER,
  MailColumnModel,
  MailLabelModel,
  PriorityType,
  QueryFilterModel,
  RequestModel,
  SupervisedCsrModel,
  TeamModel,
} from './mail-basket.model';
import { MailBasketService } from './mail-basket.service';

const MODULE_NAME = 'mailBasket';

/**
 * length of mail preview by default
 * @type {number}
 */
const MAIL_CONTENT_DISPLAY_LIMIT_DEFAULT = 300;

class MailBasketCtrl implements IComponentController {
  public name = MODULE_NAME;

  public BasketCategoryType = BasketCategoryType;
  public EpticaRequestType = EpticaRequestType;

  // store value
  public categorySelected: BasketCategoryType;
  public labelSelected: string;
  public isLoading: boolean;
  public isSupFeatureEnabled: boolean;
  public currentPage: number;
  public pageSize: number;
  public availableSkills: Chosen<Skill>[];
  public requests: RequestModel[];
  public columns: MailColumnModel[];
  public personalLabel: MailLabelModel[];
  public nbCommonRequests: number;
  public nbPersonalRequests: number;
  public nbAllRequests: number;
  public nbTotalCommonRequests: number;
  public nbTotalPersonalRequests: number;
  public nbTotalAllRequests: number;
  public nbRequests: number;
  public sort: Sort;
  public showPagination: boolean;

  public pullEnabled: boolean;
  public enableMassAction = false;
  public hasDelegators: boolean = false;

  public commonBasketTeam?: TeamModel;
  public delegatorCsr?: DelegatorCsrModel;
  public supervisedTeam?: TeamModel;
  public supervisedCsr?: SupervisedCsrModel;
  public sendBeginTime: Date;
  public sendEndTime: Date;
  public isSendPeriodFilterEnabled: boolean;

  // filters
  public hasFilters: boolean;
  public isFilterTouched = false;
  public isFilterDisabled = false;

  get subject(): string {
    return this._subject;
  }
  set subject(value: string) {
    this.isFilterTouched = this.isFilterTouched || this._subject !== value;
    this._subject = value;
  }
  public _subject: string;

  get requestId(): string {
    return this._requestId;
  }
  set requestId(value: string) {
    this.isFilterTouched = this.isFilterTouched || this._requestId !== value;
    this._requestId = value;
  }
  public _requestId: string;

  get domain(): string {
    return this._domain;
  }
  set domain(value: string) {
    this.isFilterTouched = this.isFilterTouched || this._domain !== value;
    this._domain = value;
  }
  public _domain: string;

  get skills(): Skill[] {
    return this._skills;
  }
  set skills(value: Skill[]) {
    this.isFilterTouched = this.isFilterTouched || JSON.stringify(this._skills) !== JSON.stringify(value);
    this._skills = value;
  }
  public _skills: Skill[];

  get attachmentEnabled(): boolean {
    return this._attachmentEnabled;
  }
  set attachmentEnabled(value: boolean) {
    this.isFilterTouched = this.isFilterTouched || this._attachmentEnabled !== value;
    this._attachmentEnabled = value;
  }
  public _attachmentEnabled: boolean;

  get attachmentActive(): boolean {
    return this._attachmentActive;
  }
  set attachmentActive(value: boolean) {
    this.isFilterTouched = this.isFilterTouched || this._attachmentActive !== value;
    this._attachmentActive = value;
  }
  public _attachmentActive: boolean;

  get bookmark(): boolean {
    return this._bookmark;
  }
  set bookmark(value: boolean) {
    this.isFilterTouched = this.isFilterTouched || this._bookmark !== value;
    this._bookmark = value;
  }
  public _bookmark: boolean;

  get priorities(): PriorityType[] {
    return this._priorities;
  }
  set priorities(value: PriorityType[]) {
    this.isFilterTouched = this.isFilterTouched || JSON.stringify(this._priorities) !== JSON.stringify(value);
    this._priorities = value;
  }
  public _priorities: PriorityType[];

  get beginTime(): Date {
    return this._beginTime;
  }
  set beginTime(value: Date) {
    this._beginTime = value;
  }
  public _beginTime: Date;

  get endTime(): Date {
    return this._endTime;
  }
  set endTime(value: Date) {
    this._endTime = value;
  }
  public _endTime: Date;

  get isPeriodFilterEnabled(): boolean {
    return this._isPeriodFilterEnabled;
  }
  set isPeriodFilterEnabled(value: boolean) {
    this.isFilterTouched = this.isFilterTouched || this._isPeriodFilterEnabled !== value;
    this._isPeriodFilterEnabled = value;
  }
  public _isPeriodFilterEnabled: boolean;

  get recipient(): string {
    return this._recipient;
  }
  set recipient(value: string) {
    this.isFilterTouched = this.isFilterTouched || this._recipient !== value;
    this._recipient = value;
  }
  public _recipient: string;

  get reason(): string {
    return this._reason;
  }
  set reason(value: string) {
    this.isFilterTouched = this.isFilterTouched || this._reason !== value;
    this._reason = value;
  }
  public _reason: string;

  // internal
  public currentCsrName: string;
  public allSelected: boolean;
  public oneSelected: boolean;
  public filterState: boolean;
  public normalPriority: boolean;
  public mediumPriority: boolean;
  public highPriority: boolean;
  public prioritieTypes = Object.keys(PriorityType);
  public openingRequest = false;
  public delegatorCsrList: DelegatorCsrModel[];
  public supervisedCsrList: SupervisedCsrModel[];
  public supervisedCsrListFiltered: SupervisedCsrModel[];
  public supervisorBasketTeamList: TeamModel[];
  public commonBasketTeamList: TeamModel[];

  // configuration
  readonly printOwiReason = this.configuration.basket?.owiReason ?? true;
  readonly openingRequestLockInMillis = this.configuration.basket?.openingRequestLockInMillis ?? 2000;
  readonly showMailContent = this.configuration.basket?.mailContentPreview ?? true;
  readonly mailContentDisplayLimit = this.configuration.basket?.mailContentPreviewSize ?? MAIL_CONTENT_DISPLAY_LIMIT_DEFAULT;

  constructor(
    private readonly $i18next: i18n,
    private readonly $uibModal: any,
    private readonly basketStore: IBasketStore,
    private readonly timeUtil: MomentService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly outgoingMailDeprecatedFacade: IOutgoingMailDeprecatedFacade,
    private readonly toasterFacade: ToasterFacade,
    private readonly loaderDeprecatedService: LoaderDeprecatedService,
    private readonly agentMailFacade: AgentMailFacade,
    private readonly agentUserFacade: AgentUserFacade,
    private readonly profileService: ProfileService,
    private readonly profileFacade: ProfileFacade,
    private readonly configuration: Configuration,
    private readonly search: ISearchService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly modalService: ModalService,
    private readonly agentHistory: IContactHistoryService,
    private readonly mailBasketService: MailBasketService,
    private readonly delegatorFacade: DelegatorFacade,
    private readonly mailUnitFacade: MailUnitFacade,
    private readonly mailFacade: MailFacade
  ) {}

  idEquals(a, b) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.some(id => id === b.find(_id => (_id = id)));
  }

  $onInit(): void {
    this.isSupFeatureEnabled = this.sessionDeprecatedService.isSupFeatureEnabled;

    this.profileService
      .hasPermission$('CSR_MAIL_INCOMING::basket:mass_action')
      .pipe(ng1Destroy(this))
      .subscribe(right => (this.enableMassAction = right));

    combineLatest([this.profileFacade.firstname$, this.profileFacade.lastname$])
      .pipe(ng1Destroy(this))
      .subscribe(([firstName, lastName]) => (this.currentCsrName = UserUtil.fullname({ firstName, lastName }) || ''));

    // on category selected
    this.basketStore.pipe(fromMailBasket.selectCategorySelected(), ng1Destroy(this)).subscribe(newCategory => {
      this.applyQuickFiltersForCurrentBasket(newCategory);
      this.categorySelected = newCategory;
      this.isFilterDisabled = newCategory === BasketCategoryType.DRAFT;
    });

    // label selected
    this.basketStore.pipe(fromMailBasket.selectLabelSelected(), ng1Destroy(this)).subscribe(label => (this.labelSelected = label));

    // loading
    this.basketStore.pipe(fromMailBasket.selectCurrentLoading(), ng1Destroy(this)).subscribe(loading => (this.isLoading = loading));

    // current page
    this.basketStore.pipe(fromMailBasket.selectCurrentPage(), ng1Destroy(this)).subscribe(currentPage => (this.currentPage = currentPage));

    // page size
    this.basketStore.pipe(fromMailBasket.selectCurrentPageSize(), ng1Destroy(this)).subscribe(pageSize => (this.pageSize = pageSize));

    // show pagination
    this.basketStore
      .pipe(fromMailBasket.selectCurrentShowPagination(), ng1Destroy(this))
      .subscribe(showPagination => (this.showPagination = showPagination));

    // requests model
    const request$ = this.basketStore.pipe(fromMailBasket.selectCurrentRequests<RequestModel>());

    // requests
    request$.pipe(ng1Destroy(this)).subscribe(requests => (this.requests = requests));

    // all selected
    request$
      .pipe(
        map(
          requests =>
            requests.length > 0 &&
            this.idEquals(
              this.getApprovalAndOpenRequestsLength(requests.filter(request => request.selected)),
              this.getApprovalAndOpenRequestsLength(requests)
            )
        ),
        ng1Destroy(this)
      )
      .subscribe(allSelected => (this.allSelected = allSelected));

    // one selected
    request$
      .pipe(
        map(requests => requests.some(request => request.selected)),
        ng1Destroy(this)
      )
      .subscribe(oneSelected => (this.oneSelected = oneSelected));

    // columns
    this.basketStore
      .pipe(fromMailBasket.selectCurrentColumns<MailColumnModel>(), ng1Destroy(this))
      .subscribe(columns => (this.columns = columns));

    // query filters
    this.basketStore.pipe(fromMailBasket.selectQueryFilters(), ng1Destroy(this)).subscribe(filters => {
      this.hasFilters = filters.hasFilters;
      this.isFilterTouched = filters.hasFilters;
      this.subject = filters.subject;
      this.requestId = filters.requestId;
      this.domain = filters.domain;
      this.skills = filters.skills;
      this.attachmentEnabled = filters.attachmentEnabled;
      this.attachmentActive = filters.attachmentActive;
      this.priorities = filters.priorities;
      this.bookmark = filters.bookmark;
      this.beginTime = filters.beginTime;
      this.endTime = filters.endTime;
      this.isPeriodFilterEnabled = filters.isPeriodFilterEnabled;
      this.recipient = filters.recipient;
      if (this.printOwiReason) {
        this.reason = filters.reason;
      }
      this.availableSkills =
        this.availableSkills?.map(availableSkill => {
          const isChosen = this.skills.some(skill => skill.id === availableSkill.id);
          return {
            ...availableSkill,
            chosen: isChosen,
            selected: isChosen,
          };
        }) ?? [];
    });

    // personal filters
    this.basketStore
      .pipe(fromMailBasket.selectLabelByCategory<MailLabelModel>(ChannelType.MAIL, BasketCategoryType.PERSONAL), ng1Destroy(this))
      .subscribe(personalLabels => (this.personalLabel = personalLabels));

    // nb common requests
    this.basketStore
      .pipe(fromMailBasket.selectLabelCountByType(ChannelType.MAIL, EpticaRequestType.ASSIGNABLE), ng1Destroy(this))
      .subscribe(count => (this.nbCommonRequests = count));

    // nb personal requests
    this.basketStore
      .pipe(fromMailBasket.selectLabelCountByType(ChannelType.MAIL, EpticaRequestType.RELATED), ng1Destroy(this))
      .subscribe(count => (this.nbPersonalRequests = count));

    // nb dedicated unassigned requests
    this.basketStore
      .pipe(fromMailBasket.selectLabelCountByType(ChannelType.MAIL, EpticaRequestType.DEDICATED_AGENT_UNASSIGNED), ng1Destroy(this))
      .subscribe(count => (this.nbAllRequests = count));

    // nb common requests
    this.basketStore
      .pipe(fromMailBasket.selectLabelTotalCountByType(ChannelType.MAIL, EpticaRequestType.ASSIGNABLE), ng1Destroy(this))
      .subscribe(count => (this.nbTotalCommonRequests = count));

    // nb personal requests
    this.basketStore
      .pipe(fromMailBasket.selectLabelTotalCountByType(ChannelType.MAIL, EpticaRequestType.RELATED), ng1Destroy(this))
      .subscribe(count => (this.nbTotalPersonalRequests = count));

    // nb dedicated unassigned all requests
    this.basketStore
      .pipe(fromMailBasket.selectLabelTotalCountByType(ChannelType.MAIL, EpticaRequestType.DEDICATED_AGENT_UNASSIGNED), ng1Destroy(this))
      .subscribe(count => (this.nbTotalAllRequests = count));

    // nb current requests
    this.basketStore.pipe(fromMailBasket.selectCurrentNbRequests(), ng1Destroy(this)).subscribe(count => (this.nbRequests = count));

    // current sort
    this.basketStore.pipe(fromMailBasket.selectCurrentSort(), ng1Destroy(this)).subscribe(sort => (this.sort = sort));

    // pull mode
    this.agentMailFacade.pull$.pipe(ng1Destroy(this)).subscribe(pull => {
      this.pullEnabled = pull;
      // if disable pull change category
      if (!this.pullEnabled && this.categorySelected === BasketCategoryType.COMMON) {
        this.changeCategory(BasketCategoryType.PERSONAL);
      }
    });

    // available skills
    this.agentUserFacade.skills$
      .pipe(
        map(skills =>
          skills
            .filter(({ channel }) => ChannelUtil.equals(channel, ChanEnum.In.Mail))
            .map(({ skill }) =>
              deepCopy({
                ...skill,
                chosen: false,
                selected: false,
                displayed: true,
              })
            )
        ),
        ng1Destroy(this)
      )
      .subscribe(skills => (this.availableSkills = skills));

    // init list of csrs the current user can see the basket of
    this.search
      .getSupervisedCsrListLight()
      .pipe(take(1), destroy(this))
      .subscribe(csrs => {
        this.supervisedCsrList = csrs
          .map(({ uuid, lastName, firstName, login, team }) => ({
            name: `${lastName} ${firstName} (${login})`,
            id: uuid,
            teamId: team?.uuid,
          }))
          .filter(({ id }) => id !== this.sessionDeprecatedService.getProfile().id)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
        this.supervisedCsrListFiltered = this.supervisedCsrList;
      });

    // get supervisor teams
    this.search
      .fetchSupervisorTeams()
      .pipe(take(1))
      .subscribe(teams => {
        this.commonBasketTeamList = teams
          .map(({ uuid, name }) => ({
            id: uuid,
            name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
        this.supervisorBasketTeamList = JSON.parse(JSON.stringify(this.commonBasketTeamList)); // deep clone of the array (without references)
      });

    // we trigger the fetch of delegators list
    this.delegatorFacade.loaded$
      .pipe(take(1), destroy(this), fFalsy()) // this filter stops the execution if the boolean sent by this.delegatorFacade.loaded$ is true (meaning it has already been loaded)
      .subscribe(() => this.delegatorFacade.load());

    this.delegatorFacade.loaded$
      .pipe(
        first(loaded => loaded), // this filters all observables until "loaded" is true, then take only one
        destroy(this),
        switchMap(() => this.delegatorFacade.delegators$)
      )
      .subscribe(mailDelegators => {
        this.delegatorCsrList = mailDelegators
          .map(({ id, name, firstname, lastname, absencePeriodStart, absencePeriodEnd }) => {
            const absence =
              absencePeriodStart &&
              absencePeriodEnd &&
              this.$i18next.t('agent:contact.workspace.basket.delegator_absence_period', {
                start: this.timeUtil.readableDateShortest(absencePeriodStart),
                end: this.timeUtil.readableDateShortest(absencePeriodEnd),
              });
            const absenceFormatted = absence && `(${absence})`;
            let label;
            let sortName;
            if (absenceFormatted !== 0) {
              label = joiner([name, absenceFormatted], ' ');
              sortName = joiner([lastname, firstname], ' ');
            } else {
              label = '';
              sortName = '';
            }

            return {
              name: label,
              id,
              sortName,
            };
          })
          .sort((a, b) => a.sortName.localeCompare(b.sortName, undefined, { sensitivity: 'base', numeric: true }));
        // if there are delegators we select the first one in the list
        if (this.delegatorCsrList.length > 0) {
          this.hasDelegators = true;
          this.delegatorCsr = this.delegatorCsrList[0];
          this.applyDelegatedBasketQuickFilter();
        }
      });
  }

  public readableDateTimeWithPipe(dateToConvert: Date): string {
    return this.timeUtil.readableDateTimeWithPipe(dateToConvert);
  }

  public resetFilters(): void {
    this.basketStore.dispatch(new MailBasketAction.ResetQueryFilter());
    this.refreshBasket();
    this.isFilterTouched = false;
  }

  public applyFilters(): void {
    if (this.isFilterTouched) {
      const filterModel = this.toFilterModel();
      const filters = this.mailBasketService.toBasketFilter(filterModel);
      const hasFilter = filters.filter(f => !f.isEmpty()).length;
      if (hasFilter > 0) {
        this.basketStore.dispatch(new MailBasketAction.ChangeQueryFilter(filterModel));
        this.refreshBasket();
      } else {
        this.resetFilters();
      }
    }
  }

  /**
   * Resets quick filters when the page has no quick filter UI elements (i.e. when the page is different from
   * DELEGATED, COMMON and PERSONAL_FOR_CSR category types)
   */
  applyQuickFiltersForCurrentBasket(newCategory: BasketCategoryType) {
    switch (newCategory) {
      case BasketCategoryType.COMMON:
        if (this.commonBasketTeam) this.applyCommonBasketQuickFilter();
        break;
      case BasketCategoryType.SENT:
        if (this.isSendPeriodFilterEnabled) this.applySentBasketQuickFilter();
        break;
      case BasketCategoryType.DELEGATED:
        if (this.delegatorCsr) this.applyDelegatedBasketQuickFilter();
        break;
      case BasketCategoryType.PERSONAL_FOR_CSR:
        if (this.supervisedTeam || this.supervisedCsr) this.applySupervisorBasketQuickFilter();
        break;
      default:
        this.resetQuickFilters();
        break;
    }
  }

  public applySupervisorBasketQuickFilter(): void {
    if (!this.supervisedCsr?.id) {
      this.supervisedCsr = undefined;
    }
    this.basketStore.dispatch(
      new MailBasketAction.ChangeQuickFilter({
        ...INITIAL_STATE_QUICK_FILTER,
        supervisedTeamId: this.supervisedTeam?.id,
        supervisedCsrId: this.supervisedCsr?.id,
        hasFilters: true,
      })
    );
    this.refreshBasket();
  }

  public applyDelegatedBasketQuickFilter(): void {
    this.basketStore.dispatch(
      new MailBasketAction.ChangeQuickFilter({
        ...INITIAL_STATE_QUICK_FILTER,
        delegatorCsrId: this.delegatorCsr?.id,
        hasFilters: true,
      })
    );
    this.refreshBasket();
  }

  public applyCommonBasketQuickFilter(): void {
    this.basketStore.dispatch(
      new MailBasketAction.ChangeQuickFilter({
        ...INITIAL_STATE_QUICK_FILTER,
        teamId: this.commonBasketTeam?.id,
        hasFilters: true,
      })
    );
    this.refreshBasket();
  }

  public applySentBasketQuickFilter(): void {
    this.basketStore.dispatch(
      new MailBasketAction.ChangeQuickFilter({
        ...INITIAL_STATE_QUICK_FILTER,
        beginTime: this.sendBeginTime,
        endTime: this.sendEndTime,
        isSendPeriodFilterEnabled: this.isSendPeriodFilterEnabled,
        hasFilters: true,
      })
    );
    this.refreshBasket();
  }

  public resetQuickFilters(): void {
    this.basketStore.dispatch(new MailBasketAction.ChangeQuickFilter(INITIAL_STATE_QUICK_FILTER));
    this.refreshBasket();
  }

  public resetSendQuickFilters(): void {
    this.sendBeginTime = new Date();
    this.sendEndTime = new Date();
    this.isSendPeriodFilterEnabled = false;
    this.resetQuickFilters();
  }

  public toFilterModel(): QueryFilterModel {
    return {
      hasFilters: this.isFilterTouched,
      subject: this.subject,
      domain: this.domain,
      skills: this.skills?.map(({ id, name }) => ({ id, name })) ?? [],
      attachmentEnabled: this.attachmentEnabled,
      attachmentActive: this.attachmentActive,
      priorities: this.priorities,
      bookmark: this.bookmark,
      beginTime: this.beginTime,
      endTime: this.endTime,
      isPeriodFilterEnabled: this.isPeriodFilterEnabled,
      recipient: this.recipient,
      reason: this.reason,
      requestId: this.requestId,
    };
  }

  public changeCategory(category: BasketCategoryType): void {
    this.basketStore.dispatch(new MailBasketAction.ChangeCategory(category));
  }

  public changePage(): void {
    this.basketStore.dispatch(new MailBasketAction.ChangePage(this.currentPage));
  }

  public changeLabel(model: MailLabelModel): void {
    if (!model || model.label === this.labelSelected) {
      return;
    }
    this.basketStore.dispatch(new MailBasketAction.ChangeLabel(model.label));
  }

  public refreshBasket(sortKey?: string): void {
    this.basketStore.dispatch(new MailBasketAction.ManualRefresh(sortKey));
  }

  public changeSort(sortKey: string): void {
    if (!sortKey || this.categorySelected === this.BasketCategoryType.DRAFT) {
      return;
    }
    this.refreshBasket(sortKey);
  }

  public getSkillNames(request: RequestModel): string[] {
    return request.skillList.map(skill => skill.name).join(', ');
  }

  public getObject(request: RequestModel) {
    let el = document.createElement('textarea');
    el.innerHTML = joiner([request.object, request.id], ' - ');
    return el.value;
  }

  public getRecipient(request: RequestModel): string {
    return request.recipient;
  }

  public getReason(request: RequestModel): string {
    return request.reason;
  }

  public getMemoText(request: RequestModel): string {
    return request.memo.message;
  }

  public isCategory(category: BasketCategoryType): boolean {
    return this.categorySelected === category;
  }

  public showTableAction(): boolean {
    return this.sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::basket:mass_action') || this.hasCsrSelection();
  }

  public isMassActionEnabled(): boolean {
    return (
      this.enableMassAction &&
      !(
        this.isLabelEnabled({ label: EpticaRequestType.APPROVAL_IN_PROCESS }) ||
        this.isCategory(BasketCategoryType.DRAFT) ||
        this.isCategory(BasketCategoryType.SENT)
      )
    );
  }

  public isLabelEnabled({ label }): boolean {
    return this.labelSelected === label;
  }

  public checkPriority(priority): boolean {
    return this._priorities.indexOf(priority) > -1;
  }

  public hasCsrSelection(): boolean {
    return this.isCategory(BasketCategoryType.PERSONAL_FOR_CSR) || this.isCategory(BasketCategoryType.DELEGATED);
  }

  public getBookmarkClass(bookmark: boolean): string {
    return bookmark ? 'bookmarkHigh' : 'bookmarkLow';
  }

  public getBookmarkLabel(bookmark: boolean): string {
    return bookmark ? this.$i18next.t('commons:basic.bookmark.titleHigh') : this.$i18next.t('commons:basic.bookmark.titleLow');
  }

  public canOpenMailInSentBasket(request: RequestModel): boolean {
    return request.state !== 'closed' && request.isCurrentCsrResponsible;
  }

  public getRequestContentWithLimitedCharacters(request: RequestModel): string {
    if (!request.content || !MailBasketCtrl.stripHtml(request.content).trim()) {
      return this.$i18next.t('agent:contact.workspace.basket.mail.empty_content');
    }

    if (request.content.length < this.mailContentDisplayLimit) {
      return request.content;
    }

    return request.content.substring(0, this.mailContentDisplayLimit).concat('...');
  }

  public getObjectNoteText(request: RequestModel): string {
    try {
      return decodeURIComponent(escape(request.transferMemo.message));
    } catch (_ignore) {
      return request.transferMemo.message;
    }
  }

  public getMemoDate(request: RequestModel): string {
    return request.memo.date;
  }

  public getReadableMemoDate(request: RequestModel): string {
    const differDate = request.memo.date;
    return this.timeUtil.readableDateTimeWithSecNoSep(+differDate);
  }
  public getReadableTransferMemoDate(request: RequestModel): string {
    let transferDate = request.transferMemo.date;
    return this.timeUtil.readableDateShort(+transferDate);
  }
  public getObjectNoteDate(request: RequestModel): string {
    const differDate = this.getMemoDate(request);
    const transferDate = request.transferMemo.date;
    const differText = this.getMemoText(request);
    if (!!differText && differText !== '' && differDate > transferDate) {
      return this.timeUtil.readableDateTime(+differDate);
    }
    return this.timeUtil.readableDateTime(+transferDate);
  }

  public getApprovalRequestDateText(request: RequestModel): string {
    return (
      this.$i18next.t('agent:contact.workspace.basket.approval_request_date') + ': ' + this.readableDateTimeWithPipe(request.lastEventDate)
    );
  }

  public defaultActionRequest(request: RequestModel, fromButton?: boolean): void {
    // No default line action on MSU draft (which have no request id)
    if (!request.id && !fromButton && !(this.isCategory(BasketCategoryType.DRAFT) && request.draftUuid)) {
      return;
    }
    if (this.approvalInProcess(request)) {
      return;
    }
    if (this.isCategory(BasketCategoryType.PERSONAL_FOR_CSR)) {
      this.giveupRequest(request);
    } else {
      if (!this.openingRequest) {
        this.openingRequest = true;
        setTimeout(() => {
          this.openingRequest = false;
        }, this.openingRequestLockInMillis);
        if (this.labelSelected !== EpticaRequestType.APPROVAL_IN_PROCESS) {
          if (this.isCategory(BasketCategoryType.SENT) && !fromButton) {
            this.openHistoryPopInActionRequest(request);
          } else if (request.types.includes(EpticaRequestType.DRAFT) && !!request.draftUuid) {
            this.mailUnitFacade.open({
              customerId: request.customerId,
              customerExternalId: '',
              mail: request.customerEmail,
              draftId: request.draftUuid,
            });
          } else {
            this.basketStore.dispatch(new MailBasketAction.OpenRequest(request));
          }
        }
      }
    }
  }

  public approvalInProcess(request: RequestModel) {
    return (
      this.isCategory(BasketCategoryType.PERSONAL) &&
      request.types.includes(EpticaRequestType.APPROVAL_IN_PROCESS) &&
      !this.sessionDeprecatedService.isSupFeatureEnabled
    );
  }

  public changeBookmark(request: RequestModel): void {
    const change: Partial<RequestModel> = { bookmark: !request.bookmark };
    this.basketStore.dispatch(new MailBasketAction.ChangeBookmark(request.id, !request.bookmark));
    this.basketStore.dispatch(new MailBasketAction.UpdateRequest(request.id, change));
  }

  public openHistoryPopInActionRequest(request: RequestModel): void {
    this.agentHistory.searchByEpticaRequestIds([request.id]).subscribe(interaction => {
      if (interaction && !!interaction.contactEntries && interaction.contactEntries.length > 0) {
        this.openHistoPopin(interaction.contactEntries[0]);
      }
    });
  }

  public getRequestTypeLabel(request: RequestModel): string {
    const { types } = request;
    if (
      this.labelSelected === EpticaRequestType.RELATED ||
      this.labelSelected === EpticaRequestType.RELATED_FOR_SUP_CSR ||
      this.labelSelected === EpticaRequestType.DRAFT ||
      this.labelSelected === EpticaRequestType.DELEGATED
    ) {
      return joiner(
        types.map(type => {
          switch (type) {
            case EpticaRequestType.RELATED:
            case EpticaRequestType.RELATED_FOR_SUP_CSR:
            case EpticaRequestType.DRAFT:
              return this.$i18next.t('agent:contact.workspace.basket.request_type_postponed');
            default:
              return this.$i18next.t('agent:contact.workspace.basket.request_type_' + EpticaRequestType[type].toLowerCase());
          }
        }),
        ', '
      );
    } else {
      return this.$i18next.t('agent:contact.workspace.basket.request_type_' + this.labelSelected.toLowerCase());
    }
  }

  public selectAllRequest(checked: boolean): void {
    if (checked) {
      const requestIds = this.requests
        .filter(request => this.categorySelected !== BasketCategoryType.DEDICATED_AGENT_UNASSIGNED || request.state === 'open')
        .map(r => r.id);
      this.basketStore.dispatch(new MailBasketAction.SelectAll(requestIds));
    } else {
      this.basketStore.dispatch(new MailBasketAction.UnselectAll());
    }
  }

  public deselectAllRequest(): void {
    this.basketStore.dispatch(new MailBasketAction.UnselectAll());
  }

  public selectRequest(request: RequestModel): void {
    if (request.types.includes(EpticaRequestType.APPROVAL)) {
      return;
    }
    this.basketStore.dispatch(request.selected ? new MailBasketAction.Select(request.id) : new MailBasketAction.Unselect(request.id));
  }

  public isVisibleForMode(model: MailLabelModel | MailColumnModel): boolean {
    const supFeaturesEnabled = this.sessionDeprecatedService.isSupFeatureEnabled;
    const handler = supFeaturesEnabled
      ? (supModel: MailLabelModel | MailColumnModel) => supModel.visibleForSup
      : (agentModel: MailLabelModel | MailColumnModel) => agentModel.visibleForAgent;
    if (model.label === 'reason') {
      return handler(model) && this.printOwiReason;
    }
    return handler(model);
  }

  public giveup(): void {
    this.mailFacade
      .openFormBounce(
        this.requests.filter(({ selected }) => selected).map(({ id }) => id),
        {
          param: {
            type: 'BOUNCE',
          },
          checkIsBooked: true,
        }
      )
      .afterEvent$.pipe(delay(1000), ng1Destroy(this))
      .subscribe(() => this.refreshBasket());
  }

  public giveupRequest(req: RequestModel): void {
    this.mailFacade
      .openFormBounce(
        this.requests.filter(request => request === req).map(({ id }) => id),
        {
          param: {
            type: 'BOUNCE',
            responseId: '',
          },
          checkIsBooked: true,
        }
      )
      .afterEvent$.pipe(delay(1000), ng1Destroy(this))
      .subscribe(() => this.refreshBasket());
  }

  public assign(): void {
    this.mailFacade
      .confirmAssign(
        this.requests.filter(({ selected }) => selected).map(({ id }) => id),
        {
          param: {},
          checkIsBooked: true,
        }
      )
      .afterEvent$.pipe(delay(1000), ng1Destroy(this))
      .subscribe(() => this.unselectAndRefreshBasket());
  }

  public transfer(): void {
    this.mailFacade
      .openFormForward(
        this.requests.filter(({ selected }) => selected).map(({ id }) => id),
        {
          checkIsBooked: true,
        }
      )
      .afterEvent$.pipe(delay(1000), ng1Destroy(this))
      .subscribe(() => this.unselectAndRefreshBasket());
  }

  public close(): void {
    this.mailFacade
      .openFormClose(
        this.requests.filter(({ selected }) => selected).map(({ id }) => id),
        {
          param: {},
          checkIsBooked: true,
        }
      )
      .afterEvent$.pipe(delay(1000), ng1Destroy(this))
      .subscribe(() => this.unselectAndRefreshBasket());
  }

  private unselectAndRefreshBasket(sortKey?: string): void {
    this.unselect();
    this.refreshBasket(sortKey);
  }

  private unselect(): void {
    this.requests
      .filter(({ selected }) => selected)
      .map(({ id }) => id)
      .forEach(id => this.basketStore.dispatch(new MailBasketAction.Unselect(id)));
  }

  public togglePriority(priority: PriorityType) {
    let idx = this.priorities.indexOf(priority);
    if (idx === -1) {
      this.priorities.push(priority);
    } else {
      this.priorities = this.priorities.filter(p => p !== priority);
    }
  }

  public onSkillChanges() {
    this.isFilterTouched = this.isFilterTouched || this.skills.length !== 0;
  }

  public getPriorityClass(priority: PriorityType): string {
    switch (priority) {
      case PriorityType.HIGH:
        return 'priorityHigh';
      case PriorityType.MEDIUM:
        return 'priorityMedium';
      case PriorityType.NORMAL:
      default:
        return '';
    }
  }

  public getPriorityLabel(priority: PriorityType): string {
    switch (priority) {
      case PriorityType.HIGH:
        return this.$i18next.t(`commons:basic.priority.high`);
      case PriorityType.MEDIUM:
        return this.$i18next.t(`commons:basic.priority.medium`);
      case PriorityType.NORMAL:
      default:
        return this.$i18next.t(`commons:basic.priority.normal`);
    }
  }

  public onSupervisedTeamChange() {
    // supervised team and supervised csr are linked. If team changes, then the agent must be reset.
    if (this.supervisedCsr) this.supervisedCsr = undefined;
    if (!this.supervisedTeam?.id) this.supervisedTeam = undefined;
    this.filterSupervisedCsrsByTeam();
    this.applySupervisorBasketQuickFilter();
  }

  filterSupervisedCsrsByTeam() {
    if (this.supervisedTeam) {
      if (this.supervisedTeam.id) {
        this.supervisedCsrListFiltered = this.supervisedCsrList
          .filter(({ teamId }) => this.supervisedTeam?.id === teamId)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
      }
    } else {
      this.supervisedCsrListFiltered = this.supervisedCsrList;
    }
  }

  public canOpenMail(request: RequestModel): boolean {
    return (
      (!this.openingRequest && !!request.id) ||
      (this.outgoingMailDeprecatedFacade.canOpenOutgoingMail() && !this.outgoingMailDeprecatedFacade.isMailOutgoingInProgress())
    );
  }

  public getDefaultActionTitle(request: RequestModel): string {
    if (!request.id && this.outgoingMailDeprecatedFacade.isMailOutgoingInProgress()) {
      return this.$i18next.t('agent:contact.workspace.basket.outgoing_mail_already_open');
    }
    return '';
  }

  private static stripHtml(html: string): string {
    return html.replace(/<[^>]*>?/gm, '');
  }

  private getApprovalAndOpenRequestsLength(requestModel: RequestModel[]) {
    return requestModel
      .filter(request => !request.types.includes(EpticaRequestType.APPROVAL))
      .filter(request => this.categorySelected !== BasketCategoryType.DEDICATED_AGENT_UNASSIGNED || request.state === 'open');
  }

  private openHistoPopin(element: IContactEntry) {
    const modalParam = {
      keyboard: false,
      backdrop: false,
      fullscreen: true,
      ctrlKeyboard: true,
      component: 'contactContactViewHistoPopin',
      modalId: `histoModalInteraction_${element.contactUuid}`,
      title: `${this.$i18next.t('agent:contact.info.popin.title')} ${element.contactLastName} ${element.contactFirstName}`,
      resolve: {
        selectContactEntry: () => TimelineEntry.fromContactEntry(element),
        contactCalling: () => Option.of(undefined),
        checkVisibilityMail: () => Option.of(false),
        inProcessActionsAllowed: () => Option.of(true),
      },
    };

    this.modalService.open(modalParam);
  }
}

export class MailBasket implements IComponentOptions {
  templateUrl = templateUrl;
  controller = [
    '$i18next',
    '$uibModal',
    'basketStore',
    'momentService',
    'epticaDeprecatedService',
    'outgoingMailDeprecatedService',
    'toasterFacade',
    'loaderDeprecatedService',
    'agentMailFacade',
    'agentUserFacade',
    'profileService',
    'profileFacade',
    'configuration',
    'search',
    'sessionDeprecatedService',
    'modalService',
    'agentHistory',
    'mailBasketService',
    'delegatorFacade',
    'mailUnitFacade',
    'mailFacade',
    MailBasketCtrl,
  ];
  bindings = {} as { [binding: string]: string };
}

export default MODULE_NAME;
